import { useLocalSearchParams } from 'expo-router';
import React, { useCallback, useMemo, useState } from 'react';
import { FlatList, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { PlayerAvatar } from '@/components/match/PlayerRatings';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Screen } from '@/components/ui/Screen';
import { Segmented } from '@/components/ui/Segmented';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  useFantasySeasons,
  useGameweekLeaderboard,
  useGameweeks,
  useMyFantasyTeam,
  useOverallLeaderboard,
} from '@/features/fantasy/hooks';
import { useAccaLeaderboard } from '@/features/games/acca';
import { useScorePredictBoard } from '@/features/games/scorePredict';
import { useSurvivorLeaderboard } from '@/features/games/survivor';
import { useTotrLeaderboard } from '@/features/games/teamOfRound';
import { useBracketLeaderboard } from '@/features/predictor/hooks';
import { usePredictionLeaderboard } from '@/features/predictions/game';
import { useAuth } from '@/lib/auth/AuthProvider';
import { formatNumber, latinNumerals } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

const PODIUM = ['#F4C84B', '#C7CDDA', '#CD8B53'];

interface Row {
  key: string;
  rank: number;
  name: string;
  subtitle?: string;
  avatar: string | null;
  points: number;
  isMine: boolean;
}

/** One memoized leaderboard row — its UI is identical to the old `.map()` body. */
const BoardRow = React.memo(function BoardRow({
  row,
  index,
  nums,
  youLabel,
}: {
  row: Row;
  index: number;
  nums: ReturnType<typeof latinNumerals>;
  youLabel: string;
}) {
  const medal = row.rank <= 3 ? PODIUM[row.rank - 1] : null;
  return (
    <View
      className={`flex-row items-center gap-3 px-4 py-3 ${index > 0 ? 'border-t border-line' : ''} ${
        row.isMine ? 'bg-volt/10' : ''
      }`}
    >
      {medal ? (
        <View className="h-7 w-7 items-center justify-center rounded-full" style={{ backgroundColor: medal }}>
          <AppText weight="bold" tabular style={{ color: '#1A1408', fontSize: 12 }}>
            {formatNumber(row.rank, nums)}
          </AppText>
        </View>
      ) : (
        <AppText variant="caption" weight="bold" tabular className="w-7 text-center text-muted">
          {formatNumber(row.rank, nums)}
        </AppText>
      )}
      <PlayerAvatar uri={row.avatar} name={row.name} size={34} />
      <View className="flex-1">
        <AppText variant="caption" weight="bold" className="text-ink" numberOfLines={1}>
          {row.name}
          {row.isMine ? `  ·  ${youLabel}` : ''}
        </AppText>
        {row.subtitle ? (
          <AppText variant="micro" className="text-muted" numberOfLines={1}>
            {row.subtitle}
          </AppText>
        ) : null}
      </View>
      <AppText variant="body" weight="bold" tabular className="text-volt-bright">
        {formatNumber(row.points, nums)}
      </AppText>
    </View>
  );
});

function BoardList({
  rows,
  isPending,
  youLabel,
}: {
  rows: Row[];
  isPending: boolean;
  youLabel: string;
}) {
  const { locale } = useLocale();
  const nums = latinNumerals(locale);
  const { t } = useTranslation();

  const renderItem = useCallback(
    ({ item, index }: { item: Row; index: number }) => (
      <BoardRow row={item} index={index} nums={nums} youLabel={youLabel} />
    ),
    [nums, youLabel],
  );

  if (isPending) {
    return (
      <View className="gap-3 px-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </View>
    );
  }
  if (rows.length === 0) {
    return (
      <EmptyState
        icon="podium-outline"
        title={t('leaderboardsHub.noDataTitle')}
        body={t('leaderboardsHub.noDataBody')}
      />
    );
  }
  // The rows keep their original single-rounded-`Card` frame: the Card gives the
  // surface + border + rounding, and the inner FlatList virtualizes the 100s of
  // rows instead of mounting them all at once (the old `.map()` did).
  return (
    <Card className="mx-4 flex-1 overflow-hidden">
      <FlatList
        data={rows}
        keyExtractor={(row) => row.key}
        renderItem={renderItem}
        contentContainerStyle={{ paddingBottom: 16 }}
        initialNumToRender={15}
        windowSize={11}
        maxToRenderPerBatch={15}
        removeClippedSubviews
      />
    </Card>
  );
}

type Tab = 'bracket' | 'predictions' | 'fantasy' | 'round' | 'totr' | 'acca' | 'survivor' | 'score';
const TAB_VALUES: Tab[] = ['bracket', 'predictions', 'fantasy', 'round', 'totr', 'acca', 'survivor', 'score'];

/**
 * The board switcher + list, with no screen chrome of its own. Shared by the
 * pushed `games/leaderboards-hub` route (wrapped in BackHeader) and the
 * top-level Leaderboard tab (wrapped in a title header), so both stay in sync.
 */
export function LeaderboardsHubBody({ initialTab }: { initialTab?: Tab }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const nums = latinNumerals(locale);
  const { userId } = useAuth();
  const [tab, setTab] = useState<Tab>(initialTab ?? 'bracket');

  const bracket = useBracketLeaderboard(tab === 'bracket');
  const predictions = usePredictionLeaderboard(tab === 'predictions');
  const acca = useAccaLeaderboard(tab === 'acca');
  const survivor = useSurvivorLeaderboard(tab === 'survivor');
  const totr = useTotrLeaderboard(tab === 'totr');
  const score = useScorePredictBoard(tab === 'score');
  const seasons = useFantasySeasons();
  const wcSeason = (seasons.data ?? []).find((s) => (s.name ?? '').toLowerCase().includes('world cup'));
  const fantasy = useOverallLeaderboard(wcSeason?.id, tab === 'fantasy');
  const myTeam = useMyFantasyTeam(wcSeason?.id);
  const gameweeks = useGameweeks(wcSeason?.id);
  const currentGw = useMemo(() => {
    const list = gameweeks.data ?? [];
    const played = list.filter((g) => g.status === 'live' || g.status === 'finished');
    return (played.length > 0 ? played : list).slice(-1)[0] ?? null;
  }, [gameweeks.data]);
  const round = useGameweekLeaderboard(currentGw?.id, tab === 'round');

  const you = t('fantasy.leaderboards.you');

  const rows = useMemo<Row[]>(() => {
    if (tab === 'bracket') {
      return (bracket.data ?? []).map((e) => ({
        key: e.user_id,
        rank: e.rank,
        name: e.display_name,
        avatar: e.avatar_url,
        points: e.points,
        isMine: e.user_id === userId,
      }));
    }
    if (tab === 'predictions') {
      return (predictions.data ?? []).map((e) => ({
        key: e.user_id,
        rank: e.rank,
        name: e.display_name,
        subtitle: t('predictions.record', {
          correct: formatNumber(e.correct, nums),
          played: formatNumber(e.played, nums),
        }),
        avatar: e.avatar_url,
        points: e.total_points,
        isMine: e.user_id === userId,
      }));
    }
    if (tab === 'acca') {
      return (acca.data ?? []).map((e) => ({
        key: e.user_id,
        rank: e.rank,
        name: e.display_name,
        subtitle: t('acca.daysWon', {
          won: formatNumber(e.days_won, nums),
          played: formatNumber(e.days_played, nums),
        }),
        avatar: e.avatar_url,
        points: e.total_points,
        isMine: e.user_id === userId,
      }));
    }
    if (tab === 'survivor') {
      return (survivor.data ?? []).map((e) => ({
        key: e.user_id,
        rank: e.rank,
        name: e.display_name,
        subtitle: e.alive ? t('survivor.stillIn') : t('survivor.knockedOut'),
        avatar: e.avatar_url,
        points: e.rounds_survived,
        isMine: e.user_id === userId,
      }));
    }
    if (tab === 'round') {
      return (round.data ?? []).map((e) => ({
        key: e.fantasy_team_id,
        rank: e.rank,
        name: e.team_name,
        subtitle: e.display_name,
        avatar: e.avatar_url,
        points: e.points,
        isMine: e.fantasy_team_id === (myTeam.data?.id ?? null),
      }));
    }
    if (tab === 'totr') {
      return (totr.data ?? []).map((e) => ({
        key: e.user_id,
        rank: e.rank,
        name: e.display_name,
        subtitle: t('totrPredict.boardLine', {
          rounds: formatNumber(e.rounds_played, nums),
          best: formatNumber(e.best_round, nums),
        }),
        avatar: e.avatar_url,
        points: e.total_points,
        isMine: e.user_id === userId,
      }));
    }
    if (tab === 'score') {
      return (score.data ?? []).map((e) => ({
        key: e.user_id,
        rank: e.rank,
        name: e.display_name,
        subtitle: t('scorePredict.exactsLine', { exacts: formatNumber(e.exacts, nums) }),
        avatar: e.avatar_url,
        points: e.points,
        isMine: e.user_id === userId,
      }));
    }
    return (fantasy.data ?? []).map((e) => ({
      key: e.fantasy_team_id,
      rank: e.rank,
      name: e.team_name,
      subtitle: e.display_name,
      avatar: e.avatar_url,
      points: e.total_points,
      isMine: e.fantasy_team_id === (myTeam.data?.id ?? null),
    }));
  }, [tab, bracket.data, predictions.data, acca.data, survivor.data, round.data, totr.data, score.data, fantasy.data, userId, myTeam.data, t, nums]);

  const isPending =
    tab === 'bracket'
      ? bracket.isPending
      : tab === 'predictions'
        ? predictions.isPending
        : tab === 'acca'
          ? acca.isPending
          : tab === 'survivor'
            ? survivor.isPending
            : tab === 'round'
              ? round.isPending
              : tab === 'totr'
                ? totr.isPending
                : tab === 'score'
                  ? score.isPending
                  : fantasy.isPending;

  return (
    <>
      <View className="px-4 pb-3">
        <Segmented
          options={[
            { value: 'acca', label: t('leaderboardsHub.acca') },
            { value: 'survivor', label: t('leaderboardsHub.survivor') },
            { value: 'bracket', label: t('leaderboardsHub.bracket') },
            { value: 'predictions', label: t('leaderboardsHub.predictions') },
            { value: 'score', label: t('leaderboardsHub.score') },
            { value: 'fantasy', label: t('leaderboardsHub.fantasy') },
            { value: 'round', label: t('leaderboardsHub.round') },
            { value: 'totr', label: t('leaderboardsHub.totr') },
          ]}
          value={tab}
          onChange={setTab}
        />
      </View>
      <View className="flex-1 pb-2">
        <BoardList rows={rows} isPending={isPending} youLabel={you} />
      </View>
    </>
  );
}

export default function LeaderboardsHubScreen() {
  const { t } = useTranslation();
  const params = useLocalSearchParams<{ tab?: string }>();
  const initialTab = TAB_VALUES.includes(params.tab as Tab) ? (params.tab as Tab) : undefined;
  return (
    <Screen>
      <BackHeader title={t('leaderboardsHub.title')} />
      <LeaderboardsHubBody initialTab={initialTab} />
    </Screen>
  );
}
